import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { WebService } from "../../Services/WebService";
import { PageInfo } from "../PageInfo";
import StaticListComponent from "../../Services/StaticListComponent";
import { StandardConst } from "../../Services/StandardConst";
import { NoRecordTemplate } from "../../Services/TableComponent";
import { Chip, Tooltip } from "@mui/material";
import { Button } from "react-bootstrap";

const BroadcastDocumentHistory = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [historyData, setHistoryData] = useState([]);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  PageInfo({ pageTitle: "Broadcast Document History" });

  const [bData] = React.useState([
    {
      title: "Broadcast Document List",
      hrefLink: "/ManageBroadcastDocument",
    },
    {
      title: "History",
      hrefLink: "",
    },
  ]);

  useEffect(() => {
    if (state?.BroadcastDocumentId) {
      fetchHistory();
    } else {
      navigate("/ManageBroadcastDocument");
    }
  }, [state]);

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const result = await WebService({
        endPoint: `Document/GetBroadcastDocumentHistory/${state.BroadcastDocumentId}`,
        method: "GET",
        dispatch,
      });

      if (result?.result) {
        setHistoryData(result.result.history || []);
        setDocumentInfo(result.result.documentInfo);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  function beautifyLocalTime(utcTimestamp) {
    const utcDate = new Date(utcTimestamp);
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    };
    return new Intl.DateTimeFormat(undefined, options).format(utcDate);
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      'Sent': { color: 'primary', label: 'Sent' },
      'Acknowledged': { color: 'success', label: 'Acknowledged' },
      'Pending': { color: 'warning', label: 'Pending' },
      'Failed': { color: 'error', label: 'Failed' },
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    
    return (
      <Chip
        label={config.label}
        size="small"
        color={config.color}
      />
    );
  };

  const columns = [
    {
      Text: "Employee Name",
      Value: "EmployeeName",
      render: (dr) => (
        <div>
          <strong>{dr.EmployeeName}</strong>
          <br />
          <small className="text-muted">{dr.EmployeeEmail}</small>
        </div>
      )
    },
    {
      Text: "Department",
      Value: "Department",
    },
    {
      Text: "Sent Date & Time",
      Value: "SentAt",
      render: (dr) => (
        <>{dr.SentAt ? beautifyLocalTime(dr.SentAt) : 'Not Sent'}</>
      )
    },
    {
      Text: "Status",
      Value: "Status",
      cssClass: "text-center",
      render: (dr) => getStatusChip(dr.Status)
    },
    {
      Text: "Acknowledged At",
      Value: "AcknowledgedAt",
      render: (dr) => (
        <>
          {dr.AcknowledgedAt ? (
            <span className="text-success">{beautifyLocalTime(dr.AcknowledgedAt)}</span>
          ) : (
            <span className="text-muted">-</span>
          )}
        </>
      )
    },
    {
      Text: "Remarks",
      Value: "Remarks",
      render: (dr) => (
        <>
          {dr.Remarks ? (
            <Tooltip title={dr.Remarks}>
              <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                {dr.Remarks}
              </span>
            </Tooltip>
          ) : (
            <span className="text-muted">-</span>
          )}
        </>
      )
    },
  ];

  const calculateStats = () => {
    if (!historyData || historyData.length === 0) {
      return {
        total: 0,
        sent: 0,
        acknowledged: 0,
        pending: 0,
        failed: 0
      };
    }

    return {
      total: historyData.length,
      sent: historyData.filter(d => d.Status === 'Sent').length,
      acknowledged: historyData.filter(d => d.Status === 'Acknowledged').length,
      pending: historyData.filter(d => d.Status === 'Pending').length,
      failed: historyData.filter(d => d.Status === 'Failed').length,
    };
  };

  const stats = calculateStats();

  return (
    <>
      {documentInfo && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-8">
                <h5 className="card-title mb-3">
                  {documentInfo.Title}
                  <Chip
                    label="Auto-Broadcast"
                    size="small"
                    color="warning"
                    className="ms-2"
                  />
                </h5>
                {documentInfo.Label && (
                  <p className="text-muted mb-2">
                    <strong>Description:</strong> {documentInfo.Label}
                  </p>
                )}
              </div>
              <div className="col-md-4 text-end">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate("/ManageBroadcastDocument")}
                >
                  ← Back to List
                </Button>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="row mt-3">
              <div className="col-md-2">
                <div className="card bg-light">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{stats.total}</h4>
                    <small className="text-muted">Total Recipients</small>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-primary text-white">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{stats.sent}</h4>
                    <small>Sent</small>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-success text-white">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{stats.acknowledged}</h4>
                    <small>Acknowledged</small>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-warning text-white">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{stats.pending}</h4>
                    <small>Pending</small>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-danger text-white">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">{stats.failed}</h4>
                    <small>Failed</small>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-info text-white">
                  <div className="card-body text-center py-2">
                    <h4 className="mb-0">
                      {stats.total > 0 
                        ? Math.round((stats.acknowledged / stats.total) * 100) 
                        : 0}%
                    </h4>
                    <small>Ack. Rate</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <StaticListComponent
        columns={columns}
        data={historyData}
        bData={bData}
        MasterPageName="Broadcast Document History"
        noRecordCss="p-0"
        noRecordFound={
          <NoRecordTemplate
            headerValue="No History Found"
            subHeaderValue="This document hasn't been sent yet"
            imageUrl={StandardConst.imageNoRecordsFound}
          />
        }
        IsAddButtonVisible={false}
        isSearchRequired={true}
        allowSerialNo={true}
      />
    </>
  );
};

export default BroadcastDocumentHistory;